import React from "react";
import { Link } from "react-router-dom";
import {
  Database,
  CheckCircle,
  Cloud,
  Boxes,
  Settings,
  ArrowRight,
  Phone,
  Activity,
} from "lucide-react";

export default function Nube() {
  const caracteristicas = [
    {
      icon: Cloud,
      titulo: "Multi-Cloud Management",
      descripcion:
        "Gestiona infraestructura en AWS, Azure, GCP y on-premise desde una sola plataforma.",
    },
    {
      icon: Activity,
      titulo: "Monitoreo AIOps",
      descripcion:
        "HPE OpsRamp con inteligencia artificial para observabilidad completa y resolución automática.",
    },
    {
      icon: Boxes,
      titulo: "Orquestación Avanzada",
      descripcion:
        "Morpheus Enterprise para provisioning automatizado y gobernanza de recursos cloud.",
    },
    {
      icon: Settings,
      titulo: "App Modernization",
      descripcion:
        "Contenedores, Kubernetes, microservicios y CI/CD para aplicaciones nativas de la nube.",
    },
  ];

  const soluciones = [
    {
      categoria: "HPE OpsRamp",
      descripcion:
        "Plataforma AIOps para monitoreo, observabilidad y automatización multi-cloud",
      capacidades: [
        "Monitoreo unificado de infraestructura híbrida",
        "Event management con correlación inteligente",
        "Service mapping automático",
        "Runbook automation y auto-remediation",
      ],
      casos:
        "Operaciones de TI complejas, ambientes híbridos, gestión de incidentes, cumplimiento de SLAs",
    },
    {
      categoria: "HPE GreenLake for Cloud Services",
      descripcion:
        "Experiencia cloud as-a-service para workloads on-premise y edge",
      capacidades: [
        "Pago por uso (pay-per-use) con flex capacity",
        "Autoservicio desde consola centralizada",
        "Integración con clouds públicos (AWS, Azure)",
        "Financiamiento flexible y billing unificado",
      ],
      casos:
        "Modernización de data centers, expansión de capacidad sin CAPEX, workloads con datos sensibles",
    },
    {
      categoria: "Morpheus Enterprise",
      descripcion:
        "Cloud orchestration and governance para ambientes multi-cloud",
      capacidades: [
        "Catálogo de servicios self-service",
        "Aprovisionamiento automatizado multi-cloud",
        "Cost governance y showback/chargeback",
        "Políticas de cumplimiento automatizadas",
      ],
      casos:
        "Equipos DevOps, proveedores de servicios, empresas con múltiples clouds, FinOps",
    },
    {
      categoria: "App Modernization Services",
      descripcion:
        "Transformación de aplicaciones legacy a arquitecturas cloud-native",
      capacidades: [
        "Containerización con Docker y Kubernetes",
        "Refactoring de monolitos a microservicios",
        "CI/CD pipelines con GitOps",
        "Migración a plataformas PaaS/CaaS",
      ],
      casos:
        "Aplicaciones legacy, agilidad de desarrollo, escalabilidad horizontal, reducción de time-to-market",
    },
    {
      categoria: "Virtualization & Private Cloud",
      descripcion:
        "Infraestructura virtualizada con HPE ProLiant, VMware y HPE SimpliVity",
      capacidades: [
        "Hipervisores VMware vSphere y Hyper-V",
        "Hyper-converged infrastructure (HCI)",
        "Storage optimization y deduplicación",
        "Backup integrado y DR",
      ],
      casos:
        "Consolidación de servidores, VDI, desarrollo/testing, ambientes aislados",
    },
  ];

  const beneficios = [
    "Reducción de 40% en costos operativos con automatización AIOps",
    "Provisioning de VMs en minutos vs días con orquestación",
    "Visibilidad completa de gastos cloud con cost governance",
    "MTTR reducido en 70% con correlación inteligente de eventos",
    "Agilidad 5x mayor en despliegue de aplicaciones",
    "Cumplimiento automatizado de políticas de seguridad y compliance",
    "ROI en menos de 12 meses con modelo as-a-service",
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/90 via-blue-900/85 to-indigo-900/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-indigo-600/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-indigo-300 font-semibold">
                CloudOps + Multi-Cloud Management
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Nube y CloudOps
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              Orquestación multi-cloud, monitoreo AIOps y modernización de
              aplicaciones con HPE
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contacto"
                className="bg-indigo-600 text-white px-8 py-4 rounded-lg hover:bg-indigo-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
              >
                Consultoría CloudOps <ArrowRight className="ml-2" />
              </Link>
              <a
                href="#soluciones"
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg hover:bg-white/20 transition-colors text-lg font-semibold inline-flex items-center justify-center border border-white/30"
              >
                Ver Soluciones
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introducción */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Gestión inteligente de ambientes cloud
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                La mayoría de empresas opera en ambientes{" "}
                <strong>multi-cloud</strong> con recursos distribuidos en AWS,
                Azure, GCP y on-premise. Gestionar esta complejidad requiere
                herramientas especializadas. Con <strong>HPE OpsRamp</strong> y{" "}
                <strong>Morpheus</strong>, obtienes una capa unificada de
                gestión y automatización.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Nuestras soluciones de <strong>CloudOps</strong> incluyen:
                monitoreo AIOps con correlación inteligente, orquestación
                automatizada de recursos, gobernanza de costos, y servicios
                profesionales para modernización de aplicaciones
                (containerización, Kubernetes, microservicios).
              </p>
              <div className="bg-indigo-50 border-l-4 border-indigo-600 p-6 rounded-lg">
                <p className="text-gray-800 font-semibold mb-2">
                  <Database className="inline mr-2 text-indigo-600" size={24} />
                  AIOps: La evolución de las operaciones
                </p>
                <p className="text-gray-600">
                  AIOps usa machine learning para analizar millones de eventos,
                  correlacionar incidentes, predecir fallos y ejecutar
                  remediación automática. Reduce el ruido operacional en 95%.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?q=80&w=2070"
                alt="Cloud Operations"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Características */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Capacidades CloudOps
            </h2>
            <p className="text-xl text-gray-600">
              Herramientas enterprise para gestión de nubes
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {caracteristicas.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                >
                  <div className="bg-indigo-50 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="text-indigo-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.titulo}
                  </h3>
                  <p className="text-gray-600">{item.descripcion}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Soluciones */}
      <section id="soluciones" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Portafolio de Soluciones Cloud
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Desde monitoreo hasta modernización de aplicaciones
            </p>
          </div>
          <div className="space-y-8">
            {soluciones.map((solucion, index) => (
              <div
                key={index}
                className="bg-gradient-to-r from-indigo-50 to-white border border-indigo-200 rounded-xl p-8 hover:shadow-xl transition-all"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="bg-indigo-100 p-3 rounded-lg">
                        <Cloud className="text-indigo-600" size={28} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {solucion.categoria}
                        </h3>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 text-lg">
                      {solucion.descripcion}
                    </p>
                    <div className="mb-4">
                      <span className="text-sm font-semibold text-gray-700">
                        Capacidades clave:
                      </span>
                      <ul className="mt-2 space-y-2">
                        {solucion.capacidades.map((capacidad, idx) => (
                          <li
                            key={idx}
                            className="flex items-center space-x-2 text-gray-600"
                          >
                            <CheckCircle
                              className="text-hpe-green flex-shrink-0"
                              size={16}
                            />
                            <span>{capacidad}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-6 border border-indigo-200">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      Casos de uso ideales:
                    </h4>
                    <p className="text-gray-600 text-sm">{solucion.casos}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-gradient-to-br from-indigo-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">Impacto en el Negocio</h2>
              <p className="text-xl text-indigo-100 mb-8">
                Organizaciones que implementan CloudOps con HPE experimentan:
              </p>
              <ul className="space-y-4">
                {beneficios.map((beneficio, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle
                      className="text-hpe-green flex-shrink-0 mt-1"
                      size={24}
                    />
                    <span className="text-lg text-indigo-50">{beneficio}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015"
                alt="Cloud Benefits"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Database className="text-indigo-600 mx-auto mb-6" size={64} />
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Optimiza tus operaciones cloud
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            ALLTI implementa soluciones CloudOps con HPE OpsRamp, Morpheus y
            servicios de modernización de aplicaciones. Llevamos tu
            infraestructura al siguiente nivel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacto"
              className="bg-indigo-600 text-white px-8 py-4 rounded-lg hover:bg-indigo-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              <Phone className="mr-2" size={20} />
              Hablar con CloudOps Expert
            </Link>
            <Link
              to="/casos-de-uso"
              className="bg-gray-100 text-gray-900 px-8 py-4 rounded-lg hover:bg-gray-200 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              Ver Casos de Éxito
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
